import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CharacteristicImage, EvaluationService } from '@core/data';
import { BehaviorSubject, forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { FormArray, FormGroup } from '@angular/forms';
import { Snackbar } from '@core/material';
import { coerceTimestampProperty } from '@core/utils';
import { Store } from '@ngxs/store';
import { makeCharacteristicImageDateGroup, PatchCharacteristicImage } from './characteristic-image-gallery.state';

export enum SaveDatesStatus {
    OK = 'OK',
    LOADING = 'LOADING',
    ERROR = 'ERROR',
}

export interface CharacteristicImageViewerDialogData {
    images: CharacteristicImage[];
    currentIndex?: number;
    groupLabel: string;
}

@Component({
    selector: 'pv-characteristic-image-viewer-dialog',
    templateUrl: 'characteristic-image-viewer.dialog.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: true,
    host: {
        class: 'pv-characteristic-image-viewer-dialog pv-fullscreen-dialog'
    }
})
export class CharacteristicImageViewerDialog implements OnInit, OnDestroy {

    @ViewChild('scrollViewport', {static: true})
    scrollViewport: CdkVirtualScrollViewport;

    currentIndex$ = new BehaviorSubject<number>(0);
    currentImage$: Observable<CharacteristicImage>;
    currentDateGroup$: Observable<FormGroup>;
    images: CharacteristicImage[];
    groupLabel: string;

    formArray = new FormArray([]);
    saveStatus$ = new BehaviorSubject<SaveDatesStatus>(SaveDatesStatus.OK);
    SaveDatesStatus = SaveDatesStatus;

    private _userSelected = false;
    private _initialSelectTimeout: any;
    private _saveSubscription: Subscription;

    constructor(
        @Inject(MAT_DIALOG_DATA) private _data: CharacteristicImageViewerDialogData,
        private _changeDetector: ChangeDetectorRef,
        private _dialog: MatDialogRef<CharacteristicImageViewerDialog>,
        private _evalService: EvaluationService,
        private _snackbar: Snackbar,
        private _store: Store,
    ){}

    ngOnInit(){

        this.images = this._data.images;
        this.groupLabel = this._data.groupLabel;

        this.images.forEach(image => this.formArray.push(makeCharacteristicImageDateGroup(image)));

        this.formArray.valueChanges.subscribe(() => this._changeDetector.markForCheck());

        this.currentIndex$.next(this._data.currentIndex || 0);

        this.currentImage$ =
            this.currentIndex$
            .pipe(
                map(index => {

                    if(
                        !Array.isArray(this.images) ||
                        this.images.length === 0
                    ) return null;

                    if(this.images[index] !== undefined) return this.images[index];
                    return this.images[0];

                }),
                tap(() => {
                    this._changeDetector.markForCheck();
                })
            );

        this.currentDateGroup$ =
            this.currentIndex$
            .pipe(
                map(index => <FormGroup>this.formArray.at(index) || <FormGroup>this.formArray.at(0))
            );

        // delay selecting image to give
        // content a chance to layout
        this._initialSelectTimeout = setTimeout(() => {
            this._initialSelectTimeout = null;
            // don't clobber a selection the user already made while we were waiting
            if(this._userSelected) return;
            this.selectImage(this._data.currentIndex || 0, false);
        }, 400);

    }

    ngOnDestroy(){
        if(this._initialSelectTimeout){
            clearTimeout(this._initialSelectTimeout);
        }
        if(this._saveSubscription){
            this._saveSubscription.unsubscribe();
        }
        this.currentIndex$.complete();
        this.saveStatus$.complete();
    }

    trackImage(index, image: CharacteristicImage){
        return image.key;
    }

    prevImage(){
        this.selectImage(this.currentIndex$.getValue() - 1);
    }

    nextImage(){
        this.selectImage(this.currentIndex$.getValue() + 1);
    }

    selectImage(index: number, manual: boolean = true){

        if(manual) this._userSelected = true;

        if(index < 0){
            index = this.images.length - 1;
        }else if(index >= this.images.length){
            index = 0;
        }

        this.currentIndex$.next(index);

        this.scrollViewport.checkViewportSize();
        let size = this.scrollViewport.getViewportSize();
        let itemsInViewport = size / 56;

        if(this.images.length > itemsInViewport){
            // center selected item
            this.scrollViewport.scrollToOffset(index * 56 - (size / 2 - 28));
        }

    }

    close(){
        this._dialog.close();
    }

    hasDirtyDates(): boolean {
        return this.formArray.controls.some(control => control.dirty);
    }

    save(){

        const dirtyGroups = this.formArray.controls.filter(control => control.dirty) as FormGroup[];

        if(dirtyGroups.length === 0) return;

        this.saveStatus$.next(SaveDatesStatus.LOADING);

        this._saveSubscription = forkJoin(
            dirtyGroups.map(group => {
                const key = group.value.key;
                const takenAt = coerceTimestampProperty(group.value.takenAt);

                return this._evalService.updateCharacteristicImage(key, takenAt)
                    .pipe(
                        map(result => ({ group, success: true, result })),
                        catchError(() => of({ group, success: false, result: null }))
                    );
            })
        ).subscribe(results => {

            const failures = results.filter(result => !result.success);

            results
                .filter(result => result.success)
                .forEach(({ group, result }) => {
                    group.markAsPristine();

                    const index = this.images.findIndex(image => image.key === result.key);
                    if(index > -1) this.images[index] = result;

                    // Keep the store in sync - this save bypasses SaveCharacteristicImageGallery entirely.
                    this._store.dispatch(new PatchCharacteristicImage(result));
                });

            if(failures.length === 0){
                this.saveStatus$.next(SaveDatesStatus.OK);
                this._snackbar.info("Characteristic image dates updated");
                this.close();
            }else{
                this.saveStatus$.next(SaveDatesStatus.ERROR);
                this._snackbar.error("Some dates could not be saved. Please try again.");
                this._changeDetector.markForCheck();
            }

        });

    }

}
